(function () {
  'use strict';

  if (customElements.get('collection-infinite-scroll')) return;

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.section = this.closest('[data-section-type="collection"]');
      this.grid = this.section && this.section.querySelector('[data-infinite-grid]');
      this.nextLink = this.querySelector('[data-infinite-next]');
      this.status = this.querySelector('[data-infinite-status]');
      this.loading = false;
      this.abortController = new AbortController();

      if (!this.grid || !this.nextLink || !this.dataset.nextUrl) return;

      this.nextLink.addEventListener('click', this.onClick.bind(this));

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadNextPage();
            }
          },
          { rootMargin: '600px 0px', threshold: 0.01 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
    }

    onClick(event) {
      if (!window.fetch || !window.DOMParser) return;
      event.preventDefault();
      this.loadNextPage();
    }

    async loadNextPage() {
      if (this.loading || !this.dataset.nextUrl) return;

      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.nextLink.setAttribute('aria-disabled', 'true');
      this.nextLink.textContent = 'Loading products…';

      try {
        const response = await fetch(this.dataset.nextUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const sectionSelector = `[data-section-id="${this.dataset.sectionId}"]`;
        const nextSection = page.querySelector(sectionSelector) || page;
        const nextGrid = nextSection.querySelector('[data-infinite-grid]');
        const nextScroller = nextSection.querySelector('collection-infinite-scroll');

        if (!nextGrid) throw new Error('The next product grid was not found.');

        const products = Array.from(nextGrid.children);
        const fragment = document.createDocumentFragment();
        products.forEach((product) => fragment.appendChild(product));
        this.grid.appendChild(fragment);

        const shown = Math.min(
          Number(this.dataset.productsTotal),
          Number(this.dataset.productsShown) + products.length
        );
        const total = Number(this.dataset.productsTotal);
        this.dataset.productsShown = String(shown);
        this.style.setProperty('--strawberry-progress', `${Math.round((shown / total) * 100)}%`);

        document.dispatchEvent(new CustomEvent('strawberry:collection:products-appended', {
          detail: { sectionId: this.dataset.sectionId, products }
        }));

        const nextUrl = nextScroller && nextScroller.dataset.nextUrl;
        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
          this.nextLink.href = nextUrl;
          this.nextLink.textContent = 'Load more products';
          this.status.textContent = `Showing ${shown} of ${total} products`;
        } else {
          this.finish(shown, total);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'More products could not be loaded. Please try again.';
          this.nextLink.textContent = 'Try again';
        }
      } finally {
        this.loading = false;
        this.setAttribute('aria-busy', 'false');
        this.nextLink.removeAttribute('aria-disabled');
      }
    }

    finish(shown, total) {
      this.dataset.nextUrl = '';
      if (this.observer) this.observer.disconnect();
      this.status.textContent = `All ${Math.max(shown, total)} products loaded`;
      this.nextLink.remove();
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
